import { useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import moment from 'moment';
import { ConfigurationGroupType, SpaPartyCapacity } from 'shared-types/SpaPackage';
import { SPA_PACKAGE_TIMESLOT_AVAILABILITY_TIMEOUT } from './consts';

export type BulkSpaPackageConfigGroupsEntry = {
  hasAvailability?: boolean;
  isBookable?: boolean;
  configurationGroups?: ConfigurationGroupType[];
  error?: boolean;
};

export type BulkSpaPackageConfigGroups = Record<string, BulkSpaPackageConfigGroupsEntry>;

export function useGetBulkSpaPackageConfigGroups(
  propertyId: number,
  languageCode: string,
  arrivalDate: string,
  numberOfGuests: number,
  packageIds: Array<number | string>,
  enabled = true,
  partyCapacity?: SpaPartyCapacity
) {
  const normalisedIds = packageIds.map(String).sort();

  return useQuery({
    queryKey: getBulkSpaPackageConfigGroupsKey(
      propertyId,
      languageCode,
      arrivalDate,
      numberOfGuests,
      normalisedIds,
      partyCapacity
    ),
    queryFn: () =>
      getBulkSpaPackageConfigGroups(
        propertyId,
        languageCode,
        arrivalDate,
        numberOfGuests,
        normalisedIds,
        partyCapacity
      ),
    cacheTime: 60000,
    staleTime: 60000,
    enabled: enabled && !!propertyId && normalisedIds.length > 0,
  });
}

export const getBulkSpaPackageConfigGroupsKey = (
  propertyId: number,
  languageCode: string,
  arrivalDate: string,
  numberOfGuests: number,
  packageIds: string[],
  partyCapacity?: SpaPartyCapacity
) => [
  'spaPackages',
  'bulkConfigGroups',
  propertyId,
  languageCode,
  arrivalDate,
  numberOfGuests,
  packageIds.join(','),
  ...(partyCapacity ? [partyCapacity] : []),
];

export const getBulkSpaPackageConfigGroups = async (
  propertyId: number,
  languageCode: string,
  arrivalDate: string,
  numberOfGuests: number,
  packageIds: Array<number | string>,
  partyCapacity?: SpaPartyCapacity
): Promise<BulkSpaPackageConfigGroups> => {
  const date = moment(arrivalDate).format('YYYY-MM-DD');
  const { data } = await request.get(
    `${propertyId}/spa-packages/${languageCode}/configuration-groups/availability`,
    {
      params: {
        date,
        numberOfGuests,
        packageIds: packageIds.map(String).join(','),
        ...(partyCapacity && { partyCapacity }),
      },
      timeout: SPA_PACKAGE_TIMESLOT_AVAILABILITY_TIMEOUT,
    }
  );

  return (data ?? {}) as BulkSpaPackageConfigGroups;
};
